import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Hex } from "viem";
import { usePublicClient } from "wagmi";
import { usePortfolioDomainDetailSlice } from "~/features/portfolio-domain";
import { useDirectTokenDeposit } from "~/features/portfolio-setup";
import type { OnboardingV1RecommendationDataSource } from "~/features/start/state/use-onboarding-v1-recommendations";
import { useStartFundingAssets } from "~/features/start/state/use-start-funding-assets";
import { canAppAuthSign, useAppAuth } from "~/lib/auth/app-auth";
import { createPrincipalWalletSet } from "~/lib/auth/principal-wallets";
import { createAuthQueryScope } from "~/lib/query-keys";
import { createBrowserPlatformApiTrpcAdapter } from "~/lib/trpc/platform-api-adapter";
import type {
  OnboardingV1AccountDataSource,
  OnboardingV1ClaimedDraft,
} from "./onboarding-v1-account-data-source";

type CreationStatus = "creating" | "error" | "idle" | "persisting" | "ready";
type DepositStatus =
  | "completing"
  | "confirming"
  | "depositing"
  | "error"
  | "idle";

interface OnboardingV1FundingControllerOptions {
  readonly active: boolean;
  readonly allowAutomaticCreate: boolean;
  readonly dataSource: OnboardingV1AccountDataSource;
  readonly draft: OnboardingV1ClaimedDraft | null;
  readonly onCompleted: () => void;
  readonly onDraftChange: (draft: OnboardingV1ClaimedDraft) => void;
  readonly recommendationDataSource?: OnboardingV1RecommendationDataSource;
}

interface FundingVault {
  readonly address: string;
  readonly chainRef: string;
  readonly vm: "evm";
}

function createInstantRequestKey(): string {
  if (typeof globalThis.crypto?.randomUUID !== "function") {
    throw new Error("Secure request-key generation is unavailable.");
  }
  return `onboarding-v1:${globalThis.crypto.randomUUID()}`;
}

function parseEvmAddress(
  value: string | null | undefined,
): `0x${string}` | null {
  return value && /^0x[a-fA-F0-9]{40}$/u.test(value)
    ? (value as `0x${string}`)
    : null;
}

function parseTransactionHash(value: string | null): Hex | null {
  return value && /^0x[a-fA-F0-9]{64}$/u.test(value) ? (value as Hex) : null;
}

export function useOnboardingV1FundingController(
  options: OnboardingV1FundingControllerOptions,
) {
  const { authenticated, capabilities, user } = useAppAuth();
  const draftRef = useRef(options.draft);
  const automaticCreateDraftRef = useRef<string | null>(null);
  const primaryChainRecoveryPortfolioRef = useRef<string | null>(null);
  const completionStartedRef = useRef(false);
  const completionAttemptedAtRef = useRef<string | null>(null);
  const [creationStatus, setCreationStatus] = useState<CreationStatus>("idle");
  const [depositStatus, setDepositStatus] = useState<DepositStatus>("idle");
  const [createdVaults, setCreatedVaults] = useState<readonly FundingVault[]>(
    [],
  );
  const [depositAmount, setDepositAmount] = useState(
    () => options.draft?.fundingIntent?.amount ?? "",
  );
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(
    () => options.draft?.fundingIntent?.assetId ?? null,
  );

  useEffect(() => {
    draftRef.current = options.draft;
  }, [options.draft]);

  useEffect(() => {
    if (depositAmount.length === 0 && options.draft?.fundingIntent?.amount) {
      setDepositAmount(options.draft.fundingIntent.amount);
    }
    if (!selectedAssetId && options.draft?.fundingIntent?.assetId) {
      setSelectedAssetId(options.draft.fundingIntent.assetId);
    }
  }, [depositAmount.length, options.draft, selectedAssetId]);

  const principalWallets = useMemo(
    () => createPrincipalWalletSet({ user, vmPreference: "evm" }),
    [user],
  );
  const evmWallet =
    principalWallets.wallets.find((wallet) => wallet.vmId === "evm") ?? null;
  const ownerAddress = parseEvmAddress(evmWallet?.address);
  const selectedChainId = options.draft?.selectedChainId ?? null;
  const platformApi = useMemo(
    () =>
      createBrowserPlatformApiTrpcAdapter({
        getVmPreference: () => "evm",
        walletAddress: ownerAddress,
      }),
    [ownerAddress],
  );
  const authQueryScope = useMemo(
    () =>
      createAuthQueryScope({
        authenticated,
        user,
        walletAddress: ownerAddress,
      }),
    [authenticated, ownerAddress, user],
  );
  const fundingAssetQuery = useStartFundingAssets({
    authenticated: options.active && authenticated,
    authQueryScope,
    onboardingApi: platformApi,
    selectedChainId: selectedChainId ?? 0,
    walletAddress: ownerAddress,
  });
  const fundingAssets = useMemo(
    () =>
      fundingAssetQuery.fundingAssets.filter(
        (asset) =>
          selectedChainId !== null &&
          asset.vm === "evm" &&
          asset.chainId === selectedChainId,
      ),
    [fundingAssetQuery.fundingAssets, selectedChainId],
  );

  useEffect(() => {
    if (!selectedAssetId && fundingAssets[0]) {
      setSelectedAssetId(fundingAssets[0].assetId);
    }
  }, [fundingAssets, selectedAssetId]);

  const selectedAsset =
    fundingAssets.find((asset) => asset.assetId === selectedAssetId) ?? null;
  const portfolioId = options.draft?.createdPortfolioId ?? null;
  const detailSlice = usePortfolioDomainDetailSlice({
    enabled: options.active && portfolioId !== null,
    portfolioId,
  });
  const canonicalVaults = useMemo<readonly FundingVault[]>(
    () =>
      detailSlice.vaults.flatMap((vault) =>
        vault.vm === "evm" && parseEvmAddress(vault.address)
          ? [
              {
                address: vault.address,
                chainRef: vault.chainRef,
                vm: "evm" as const,
              },
            ]
          : [],
      ),
    [detailSlice.vaults],
  );
  const vaults = createdVaults.length > 0 ? createdVaults : canonicalVaults;
  const activeVault =
    vaults.find((vault) => vault.chainRef === String(selectedChainId)) ?? null;
  const recipientAddress = parseEvmAddress(activeVault?.address);
  const publicClient = usePublicClient({
    chainId: selectedChainId ?? undefined,
  });

  const persistDraftPatch = useCallback(
    async (patch: Partial<OnboardingV1ClaimedDraft>) => {
      const currentDraft = draftRef.current;
      if (!currentDraft) {
        throw new Error("Claimed onboarding draft is unavailable.");
      }
      const nextDraft: OnboardingV1ClaimedDraft = {
        ...currentDraft,
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      await options.dataSource.saveClaimedDraft(nextDraft);
      draftRef.current = nextDraft;
      options.onDraftChange(nextDraft);
      return nextDraft;
    },
    [options.dataSource, options.onDraftChange],
  );

  const directDepositMutation = useDirectTokenDeposit({
    canSign: canAppAuthSign({ authenticated, capabilities }),
    chainId: selectedChainId,
    expectedSigner: ownerAddress ? { address: ownerAddress, vm: "evm" } : null,
    onIntentCreated: async (intent) => {
      await persistDraftPatch({
        depositOperationId: intent.operationId,
        fundingIntent: {
          amount: depositAmount,
          assetId: selectedAsset?.assetId ?? null,
          mode: "direct",
        },
      });
    },
    onTransactionSubmitted: async (transaction) => {
      await persistDraftPatch({
        depositOperationId: transaction.operationId,
        depositTxHash: transaction.txHash,
        fundingIntent: {
          amount: depositAmount,
          assetId: selectedAsset?.assetId ?? null,
          mode: "direct",
        },
      });
    },
    recipientAddress,
  });

  const completeConfirmedDeposit = useCallback(
    async (confirmedAt: string) => {
      if (completionStartedRef.current) {
        return;
      }
      completionStartedRef.current = true;
      completionAttemptedAtRef.current = confirmedAt;
      setDepositStatus("completing");
      try {
        const confirmedDraft = await persistDraftPatch({
          depositConfirmedAt: confirmedAt,
        });
        if (!ownerAddress) {
          throw new Error("EVM onboarding owner is unavailable.");
        }
        await options.dataSource.markOnboardingCompleted(ownerAddress);
        const completedAt = new Date().toISOString();
        await persistDraftPatch({
          completedAt,
          depositConfirmedAt: confirmedDraft.depositConfirmedAt,
          onboardingStepId: null,
          stepId: "live",
        });
        options.onCompleted();
      } catch {
        completionStartedRef.current = false;
        setDepositStatus("error");
      }
    },
    [options.dataSource, options.onCompleted, ownerAddress, persistDraftPatch],
  );

  useEffect(() => {
    const draft = options.draft;
    if (!options.active || !draft || completionStartedRef.current) {
      return;
    }
    if (draft.completedAt) {
      options.onCompleted();
      return;
    }
    if (
      draft.depositConfirmedAt &&
      completionAttemptedAtRef.current !== draft.depositConfirmedAt
    ) {
      void completeConfirmedDeposit(draft.depositConfirmedAt);
      return;
    }
    if (
      draft.depositTxHash &&
      !directDepositMutation.isPending &&
      depositStatus === "idle"
    ) {
      setDepositStatus("error");
    }
  }, [
    completeConfirmedDeposit,
    depositStatus,
    directDepositMutation.isPending,
    options.active,
    options.draft,
    options.onCompleted,
  ]);

  const beginCreation = useCallback(async () => {
    const currentDraft = draftRef.current;
    if (!currentDraft || currentDraft.createdPortfolioId) {
      return;
    }

    setCreationStatus("persisting");
    try {
      if (!ownerAddress) {
        throw new Error("EVM onboarding owner is unavailable.");
      }
      const requestKey =
        currentDraft.instantCreateRequestKey ?? createInstantRequestKey();
      if (!currentDraft.instantCreateRequestKey) {
        await persistDraftPatch({ instantCreateRequestKey: requestKey });
      }
      setCreationStatus("creating");
      const result = await options.dataSource.createInstantPortfolio({
        ownerAddress,
        requestKey,
      });
      const nextDraft = await persistDraftPatch({
        createdPortfolioId: result.portfolioId,
        selectedChainId: result.primaryChainId,
      });
      setCreatedVaults(
        result.vaults.flatMap((vault) =>
          parseEvmAddress(vault.address)
            ? [
                {
                  address: vault.address,
                  chainRef: String(vault.chainId),
                  vm: "evm" as const,
                },
              ]
            : [],
        ),
      );
      draftRef.current = nextDraft;
      setCreationStatus("ready");
    } catch {
      setCreationStatus("error");
    }
  }, [options.dataSource, ownerAddress, persistDraftPatch]);

  const recoverPrimaryChain = useCallback(async () => {
    const currentDraft = draftRef.current;
    const currentPortfolioId = currentDraft?.createdPortfolioId;
    if (
      !currentDraft ||
      !currentPortfolioId ||
      currentDraft.selectedChainId !== null
    ) {
      return;
    }

    primaryChainRecoveryPortfolioRef.current = currentPortfolioId;
    setCreationStatus("persisting");
    try {
      if (!ownerAddress) {
        throw new Error("EVM onboarding owner is unavailable.");
      }
      const primaryChainId =
        await options.dataSource.loadPortfolioPrimaryChainId({
          ownerAddress,
          portfolioId: currentPortfolioId,
        });
      if (primaryChainId === null) {
        throw new Error("Portfolio funding chain is unavailable.");
      }
      await persistDraftPatch({ selectedChainId: primaryChainId });
      setCreationStatus("ready");
    } catch {
      setCreationStatus("error");
    }
  }, [options.dataSource, ownerAddress, persistDraftPatch]);

  useEffect(() => {
    const draft = options.draft;
    if (!options.active || !draft) {
      return;
    }
    if (draft.createdPortfolioId) {
      if (draft.selectedChainId !== null) {
        setCreationStatus("ready");
        return;
      }
      if (
        ownerAddress !== null &&
        creationStatus !== "creating" &&
        creationStatus !== "persisting" &&
        primaryChainRecoveryPortfolioRef.current !== draft.createdPortfolioId
      ) {
        void recoverPrimaryChain();
      }
      return;
    }
    if (creationStatus === "creating" || creationStatus === "persisting") {
      return;
    }
    if (draft.instantCreateRequestKey || !options.allowAutomaticCreate) {
      setCreationStatus("error");
      return;
    }
    if (automaticCreateDraftRef.current !== draft.updatedAt) {
      automaticCreateDraftRef.current = draft.updatedAt;
      void beginCreation();
    }
  }, [
    beginCreation,
    creationStatus,
    options.active,
    options.allowAutomaticCreate,
    options.draft,
    ownerAddress,
    recoverPrimaryChain,
  ]);

  const submitDeposit = useCallback(async () => {
    const currentDraft = draftRef.current;
    const currentPortfolioId = currentDraft?.createdPortfolioId;
    if (
      !currentDraft ||
      !currentPortfolioId ||
      !selectedAsset ||
      selectedAsset.decimals === null ||
      selectedChainId === null ||
      !parseEvmAddress(selectedAsset.address) ||
      !recipientAddress ||
      !publicClient
    ) {
      setDepositStatus("error");
      return;
    }

    setDepositStatus("depositing");
    try {
      await persistDraftPatch({
        fundingIntent: {
          amount: depositAmount,
          assetId: selectedAsset.assetId,
          mode: "direct",
        },
      });
      const persistedTxHash = parseTransactionHash(
        draftRef.current?.depositTxHash ?? null,
      );
      const persistedOperationId = draftRef.current?.depositOperationId ?? null;
      const result = await directDepositMutation.mutateAsync({
        amount: depositAmount,
        assetId: selectedAsset.assetId,
        chainId: selectedAsset.chainId,
        decimals: selectedAsset.decimals,
        portfolioId: currentPortfolioId,
        recipientAddress,
        ...(persistedTxHash && persistedOperationId
          ? {
              resume: {
                operationId: persistedOperationId,
                txHash: persistedTxHash,
              },
            }
          : {}),
        tokenAddress: selectedAsset.address as `0x${string}`,
      });
      setDepositStatus("confirming");
      const receipt = await publicClient.waitForTransactionReceipt({
        hash: result.txHash,
      });
      if (receipt.status !== "success") {
        throw new Error("Deposit transaction reverted.");
      }
      await completeConfirmedDeposit(new Date().toISOString());
    } catch {
      setDepositStatus("error");
    }
  }, [
    completeConfirmedDeposit,
    depositAmount,
    directDepositMutation,
    persistDraftPatch,
    publicClient,
    recipientAddress,
    selectedAsset,
    selectedChainId,
  ]);

  const operationError =
    creationStatus === "error" ||
    depositStatus === "error" ||
    fundingAssetQuery.isError ||
    detailSlice.error !== null ||
    (creationStatus === "ready" &&
      !detailSlice.isLoading &&
      activeVault === null);
  const isCreating =
    creationStatus === "creating" || creationStatus === "persisting";
  const isDepositPending =
    depositStatus === "completing" ||
    depositStatus === "confirming" ||
    depositStatus === "depositing" ||
    directDepositMutation.isPending;
  const retry = () => {
    if (creationStatus === "error" && !portfolioId) {
      void beginCreation();
      return;
    }
    if (
      creationStatus === "error" &&
      portfolioId &&
      selectedChainId === null
    ) {
      primaryChainRecoveryPortfolioRef.current = null;
      void recoverPrimaryChain();
      return;
    }
    if (fundingAssetQuery.isError) {
      void fundingAssetQuery.retry();
      return;
    }
    void submitDeposit();
  };

  return {
    canSubmit:
      creationStatus === "ready" &&
      !isDepositPending &&
      depositAmount.trim().length > 0 &&
      selectedAsset !== null &&
      recipientAddress !== null,
    creationStatus,
    depositAmount,
    fundingAssets,
    isCreating,
    isDepositPending,
    isLoadingAssets: fundingAssetQuery.isLoading,
    operationError,
    recommendationDataSource: options.recommendationDataSource,
    retry,
    selectedAsset,
    selectedChainId,
    setDepositAmount,
    setSelectedAssetId,
    submitDeposit,
  };
}

export type OnboardingV1FundingController = ReturnType<
  typeof useOnboardingV1FundingController
>;
